/**
 * Who you are signed in as, and how to stop being.
 *
 * The account card every large platform puts behind the avatar: who you are, which GitHub
 * account your tools act as, where to manage it, and the way out. The GitHub line is here
 * because the two together answer the question an operator asks when a repository is
 * missing: "am I signed in, and whose GitHub is this?"
 */
import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronDown, Coins, Github, LogOut, Settings, UserRound } from 'lucide-react'

import { BuyCreditsButton } from '@/components/billing/BuyCreditsButton'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Controls'
import { useAuth } from '@/features/auth/useAuth'
import { useCredits } from '@/features/billing/useCredits'

export function AccountMenu() {
  const { user, signOut } = useAuth()
  const { data: credits, enabled: billing } = useCredits()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  // Escape closes it, as every menu on every platform does.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (!user) return null

  const label = user.display_name || user.email || `Account ${user.id}`

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-full py-1 pl-1 pr-1 text-sm text-ink
          transition-colors hover:bg-hover sm:pr-2.5"
      >
        <Avatar name={label} size="sm" />
        {/* Visually hidden on a phone, never removed: it is the button's accessible name. */}
        <span className="sr-only max-w-[12rem] truncate font-medium sm:not-sr-only">{label}</span>
        <ChevronDown aria-hidden="true" className="hidden h-4 w-4 text-ink-muted sm:block" />
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-label="Close account menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-2 w-72 origin-top-right animate-menu-in
              rounded-xl border border-line bg-overlay shadow-pop"
          >
            <div className="flex items-center gap-3 px-4 py-4">
              <Avatar name={label} size="lg" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{label}</p>
                <p className="truncate text-xs text-ink-muted">{user.email ?? '—'}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 border-y border-line bg-raised/60 px-4 py-2.5 text-xs">
              <Github aria-hidden="true" className="h-4 w-4 shrink-0 text-ink-muted" />
              {user.github_connected ? (
                <span className="min-w-0 truncate text-ink-dim">
                  Acting as <span className="mono font-medium text-ink">{user.github_login}</span>
                </span>
              ) : (
                <span className="text-waiting">No GitHub account connected</span>
              )}
            </div>

            {billing && credits ? (
              // The balance in full, where an account's details belong — the bar shows what
              // is spendable, this shows why that differs from the balance when it does.
              <div className="border-b border-line px-4 py-3" data-testid="account-credits">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-xs text-ink-muted">Credits</span>
                  <span className="text-sm font-semibold tabular-nums text-ink">
                    {credits.available_credits}
                    <span className="ml-1 text-xs font-normal text-ink-muted">available</span>
                  </span>
                </div>
                {credits.reserved_credits > 0 ? (
                  <p className="mt-1 text-xs text-ink-muted">
                    {credits.balance_credits} total · {credits.reserved_credits} held by a running
                    request
                  </p>
                ) : null}
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <BuyCreditsButton />
                  <Link
                    to="/billing"
                    onClick={() => setOpen(false)}
                    className="text-xs text-primary underline-offset-2 hover:underline"
                  >
                    History
                  </Link>
                </div>
              </div>
            ) : null}

            <div className="p-1.5">
              <Link
                to="/profile"
                onClick={() => setOpen(false)}
                className="flex h-9 items-center gap-3 rounded-md px-2.5 text-sm text-ink-dim
                  transition-colors hover:bg-hover hover:text-ink"
              >
                <UserRound aria-hidden="true" className="h-4 w-4 text-ink-muted" />
                Profile
              </Link>
              <Link
                to="/billing"
                onClick={() => setOpen(false)}
                className="flex h-9 items-center gap-3 rounded-md px-2.5 text-sm text-ink-dim
                  transition-colors hover:bg-hover hover:text-ink"
              >
                <Coins aria-hidden="true" className="h-4 w-4 text-ink-muted" />
                Credits
              </Link>
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                className="flex h-9 items-center gap-3 rounded-md px-2.5 text-sm text-ink-dim
                  transition-colors hover:bg-hover hover:text-ink"
              >
                <Settings aria-hidden="true" className="h-4 w-4 text-ink-muted" />
                Settings
              </Link>
            </div>

            <div className="border-t border-line p-3">
              <Button
                className="w-full"
                icon={LogOut}
                disabled={busy}
                onClick={() => {
                  setBusy(true)
                  void signOut().finally(() => setBusy(false))
                }}
              >
                {busy ? 'Signing out…' : 'Sign out'}
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
